import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom'
import NavigationTabs from '@/components/NavigationTabs.tsx'
import { FilesProvider } from '@/contexts/filesContext.tsx'
import { RefObject, useEffect, useMemo, useRef } from 'react'
import { useVisitedAlbumIds } from '@/stores/useVisitedAlbums.ts'
import { FileFilterProvider } from '@/contexts/fileFilter.tsx'
import { shallHideElementsForScreenshot } from '@/controller/demoHelper.ts'
import { useTranslation } from 'react-i18next'
import { useDrives } from '@/stores/useDrives.ts'
import { Album } from '@/models/album.ts'
import { HeyDrive, HeyDriveType, shouldUseGalleryNav } from '@/models/drive.ts'
import { Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import IOSHomescreenPreview from '@/components/IOSHomescreenPreview.tsx'
import { AllFilesProvider } from '@/contexts/allFilesContext.tsx'
import { getThumbnailFromAlbums } from '@/models/album.ts'
import { AppIconModal, shouldShowAppIconModal } from '@/pages/Settings.page.tsx'
import { usePwaInstall } from '@/controller/pwaInstall.ts'
import { useWebAppManifest } from '@/controller/webAppManifest.ts'
import { getHomescreenPath } from '@/controller/driveHelper.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { StatScopeProvider } from '@/contexts/statsScopeContext.tsx'
import { useAppTracking } from '@/controller/appTracking.ts'

export default function GenericDrivePage() {
  const {driveId, albumId} = useParams<{driveId?: string, albumId?: string}>()
  const [searchParams] = useSearchParams()
  const isPreview = searchParams.get('_preview') === '1'
  const location = useLocation()
  const {drives, hasVisitedAlbumId, addVisitedAlbumIds} = useVisitedAlbumIds()
  const { drives: drivesStore } = useDrives()
  const addToHomescreenModalRef = useRef<HTMLDialogElement>(null)
  const appIconModalRef = useRef<HTMLDialogElement>(null)
  const { authenticationToken } = useProfile()
  const { canPrompt: canPromptAppInstall } = usePwaInstall()

  const currentDrive = driveId ? drivesStore[driveId] : null
  const currentAlbum = useMemo(() => {
    if (!currentDrive) return null
    if (albumId) {
      return currentDrive.albums.find(a => a.id === albumId) || null
    }
    return currentDrive.albums.find(a => a.is_primary) || currentDrive.albums[0] || null
  }, [currentDrive, albumId])

  useEffect(() => {
    if (albumId && driveId && !hasVisitedAlbumId(driveId, albumId)) {
      addVisitedAlbumIds(driveId, albumId)
    }
  }, [albumId, driveId, drives, hasVisitedAlbumId, addVisitedAlbumIds]);

  useAlbumPageMeta(currentAlbum, currentDrive)

  // Only prof content is tracked — the backend drops everything else anyway, so
  // B2C galleries never even send. Same gate for the app events below.
  const isTracked = currentDrive?.drive_type === HeyDriveType.PROF
  const statScope = useMemo(
    () => ({ source: 'gallery' as const, drive: isTracked ? driveId : undefined }),
    [isTracked, driveId],
  )

  useAppTracking(isTracked ? driveId : undefined)

  const driveIconUrl = (currentDrive ? getThumbnailFromAlbums(currentDrive.albums) : null) ?? currentAlbum?.thumbnail ?? null

  // Per-drive manifest: on Chromium every gallery installs as its own app, with
  // its own icon and its own (authenticated) start URL. No-op on iOS.
  useWebAppManifest(currentDrive && driveId ? {
    name: currentDrive.title,
    // Drive root, not the current album — keeps the manifest stable while browsing.
    startUrl: `${window.location.origin}${getHomescreenPath(driveId, null, authenticationToken)}`,
    iconUrl: driveIconUrl,
    themeColor: currentDrive.color_neutral,
    backgroundColor: currentDrive.color_neutral,
  } : null)

  // Show add to homescreen modal if navigated with state
  useEffect(() => {
    const state = location.state as { showAddToHomescreen?: boolean } | null
    if (state?.showAddToHomescreen) {
      // Scroll to top to ensure Safari's bottom bar is visible
      window.scrollTo({ top: 0, behavior: 'instant' })
      addToHomescreenModalRef.current?.showModal()
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  // Prof drives: offer to add the drive as an app on first visit. On iOS that
  // leads to the manual guide, on Android straight to Chrome's install dialog.
  //
  // `canPromptAppInstall` is a dependency on purpose: Chrome only fires
  // `beforeinstallprompt` after the first tap on the page, so on Android this
  // effect usually runs a second time — as soon as the prompt is in hand — and
  // only then opens the modal. That is also the moment its button can do
  // something, so the timing is exactly right.
  useEffect(() => {
    if (!currentDrive) return
    if (currentDrive.drive_type !== HeyDriveType.PROF) return
    if (!shouldShowAppIconModal(currentDrive.id)) return

    let timeout: ReturnType<typeof setTimeout>
    const showWhenFree = () => {
      // That first tap may well have opened the lightbox or another dialog —
      // never cover something the user opened themselves, wait it out.
      if (document.querySelector('dialog[open], .yarl__root')) {
        timeout = setTimeout(showWhenFree, 1500)
        return
      }
      appIconModalRef.current?.showModal()
    }

    timeout = setTimeout(showWhenFree, 600)
    return () => clearTimeout(timeout)
  }, [currentDrive?.id, currentDrive?.drive_type, canPromptAppInstall])

  // The URL names an album this drive does not have (deleted album, stale
  // bookmark, typo). The `:albumId` route matches anything, so without this the
  // album pages would render with a null album and crash. Fall back to the
  // drive root, which resolves to the primary album.
  if (currentDrive && albumId && !currentAlbum) {
    return <Navigate to={`/d/${driveId}${location.search}`} replace />
  }

  return <StatScopeProvider scope={statScope}>
    <div className='pb-[3rem] bg-neutral h-full'>
    <FileFilterProvider>
      <AllFilesProvider>
        <FilesProvider>
          <Outlet/>
          {
            !shallHideElementsForScreenshot() && !isPreview && (location.pathname.includes('/shop') || !(currentDrive && shouldUseGalleryNav(currentDrive))) && <NavigationTabs/>
          }
          <AddToHomescreenModal
            modalRef={addToHomescreenModalRef}
            thumbnailUrl={driveIconUrl}
          />
          {
            currentDrive?.drive_type === HeyDriveType.PROF && (
              <AppIconModal
                modalRef={appIconModalRef}
                thumbnailUrl={driveIconUrl}
              />
            )
          }
        </FilesProvider>
      </AllFilesProvider>
    </FileFilterProvider>
    </div>
  </StatScopeProvider>
}

const DEFAULT_FAVICON = '/favicon.png'

function useAlbumPageMeta(currentAlbum: Album | null, currentDrive: HeyDrive | null) {
  useEffect(() => {
    const faviconUrl = (currentDrive ? getThumbnailFromAlbums(currentDrive.albums) : null) || currentAlbum?.thumbnail || DEFAULT_FAVICON

    // Update favicon
    let faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
    if (faviconLink) {
      faviconLink.href = faviconUrl
    } else {
      faviconLink = document.createElement('link')
      faviconLink.rel = 'icon'
      faviconLink.href = faviconUrl
      document.head.appendChild(faviconLink)
    }

    // Update Apple Touch Icon
    let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null
    if (appleTouchIcon) {
      appleTouchIcon.href = faviconUrl
    } else {
      appleTouchIcon = document.createElement('link')
      appleTouchIcon.rel = 'apple-touch-icon'
      appleTouchIcon.href = faviconUrl
      document.head.appendChild(appleTouchIcon)
    }

    // Update page title
    const previousTitle = document.title
    if (currentAlbum?.title && currentDrive?.title) {
      document.title = `${currentAlbum.title} - ${currentDrive.title} - FridaySnap`
    } else if (currentDrive?.title) {
      document.title = `${currentDrive.title} - FridaySnap`
    }

    // Update Apple homescreen app title (drive name only — becomes icon label on iOS)
    const appleTitleMeta = document.querySelector("meta[name='apple-mobile-web-app-title']") as HTMLMetaElement | null
    const previousAppleTitle = appleTitleMeta?.content ?? 'FridaySnap'
    if (appleTitleMeta && currentDrive?.title) {
      appleTitleMeta.content = currentDrive.title
    }

    return () => {
      const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
      if (faviconLink) {
        faviconLink.href = DEFAULT_FAVICON
      }
      const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null
      if (appleTouchIcon) {
        appleTouchIcon.href = DEFAULT_FAVICON
      }
      const appleTitleMeta = document.querySelector("meta[name='apple-mobile-web-app-title']") as HTMLMetaElement | null
      if (appleTitleMeta) {
        appleTitleMeta.content = previousAppleTitle
      }
      document.title = previousTitle
    }
  }, [currentAlbum?.thumbnail, currentAlbum?.title, currentDrive?.title])
}

interface AddToHomescreenModalProps {
  modalRef: RefObject<HTMLDialogElement>
  thumbnailUrl: string | null
}

function AddToHomescreenModal({ modalRef, thumbnailUrl }: AddToHomescreenModalProps) {
  const { t } = useTranslation()

  return (
    <Modal ref={modalRef} responsive className='bg-neutral'>
      <Modal.Header>
        {t('common.addToHomescreen')}
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col items-center gap-5 text-center'>
          <IOSHomescreenPreview thumbnailUrl={thumbnailUrl} showButton={false} />

          <div className='flex flex-col gap-5 w-full'>
            <div className='flex flex-col gap-2'>
              <div className='flex flex-row items-center gap-3'>
                <div className='flex items-center justify-center w-8 h-8 rounded-full bg-primary text-neutral font-bold shrink-0'>1</div>
                <p className='text-left flex items-center gap-1'>
                  {t('common.addToHomescreenStep1')}
                </p>
              </div>
              <img src='/step-1-icon.webp' alt='Step 1' className='w-full rounded-lg' />
            </div>
            <div className='flex flex-col gap-2'>
              <div className='flex flex-row items-center gap-3'>
                <div className='flex items-center justify-center w-8 h-8 rounded-full bg-primary text-neutral font-bold shrink-0'>2</div>
                <p className='text-left'>{t('common.addToHomescreenStep2')}</p>
              </div>
              <img src='/stap-2-icon.webp' alt='Step 2' className='w-full rounded-lg' />
            </div>
          </div>

          <form method="dialog">
            <Button color='primary'>{t('common.finishGuide')}</Button>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  )
}
